import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Package, MapPin, Upload, Check, Plus, Trash2, FileSpreadsheet, BarChart3, X, Pencil, Layers, Map, Lock, Download, History, Boxes, Globe, AlertTriangle, ArrowUp, ArrowDown, Megaphone, Image as ImageIcon, Video, Power, Store, AlertOctagon, RotateCcw, Database, Truck, Shield, Printer } from "lucide-react";
import api, { fileUrl, API, imageSrc } from "@/api";
import TopBar from "@/components/TopBar";
import UnitsDrilldown from "@/components/UnitsDrilldown";
import MissingTamilBanner from "@/components/MissingTamilBanner";
import CommandCenter from "@/pages/admin/CommandCenter";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, color, testid, onClick, hint }) => {
  const interactive = !!onClick;
  const Wrapper = interactive ? "button" : "div";
  return (
    <Wrapper
      onClick={onClick}
      data-testid={testid}
      className={`stat-card text-left transition ${interactive ? "hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] cursor-pointer" : ""}`}
    >
      <div className={`w-10 h-10 rounded-xl ${color} grid place-items-center mb-2`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="text-xs text-[#86868B] uppercase tracking-wider">{label}</div>
      <div className="heading-font text-2xl font-bold text-[#1D1D1F] mt-1">{value}</div>
      {hint && <div className="text-[10px] text-[#86868B] mt-1">{hint}</div>}
    </Wrapper>
  );
};

const TabButton = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2 rounded-xl font-bold text-sm transition ${active ? "bg-[#0055FF] text-white shadow-md" : "text-[#86868B] hover:bg-white"}`}>
    {children}
  </button>
);

export default function AdminDashboard() {
  const { t } = useLang();
  const [tab, setTab] = useState("command");
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [upiPending, setUpiPending] = useState([]);
  const [areas, setAreas] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [stockLogs, setStockLogs] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [regions, setRegions] = useState([]);
  const [popups, setPopups] = useState([]);
  const [shops, setShops] = useState([]);
  const [showAdd, setShowAdd] = useState(null);
  const [drilldown, setDrilldown] = useState(null);

  const loadOverview = () => api.get("/dashboard/super-admin").then(r => setOverview(r.data));
  const loadUsers = () => api.get("/users?status=all").then(r => setUsers(r.data));
  const loadProducts = () => api.get("/products").then(r => setProducts(r.data));
  const loadDistricts = () => api.get("/districts").then(r => setDistricts(r.data));
  const loadAreas = () => api.get("/areas").then(r => setAreas(r.data));
  const loadTerritories = () => api.get("/territories").then(r => setTerritories(r.data));
  const loadPermissions = () => api.get("/permissions").then(r => setPermissions(r.data));
  const loadAudit = () => api.get("/admin/audit-logs?limit=200").then(r => setAuditLogs(r.data));
  const loadStockHist = () => api.get("/admin/stock/history?limit=200").then(r => setStockLogs(r.data));
  const loadLowStock = () => api.get("/admin/stock/low-stock").then(r => setLowStock(r.data));
  const loadRegions = () => api.get("/regions").then(r => setRegions(r.data));
  const loadPopups = () => api.get("/admin/popups").then(r => setPopups(r.data));
  const loadShops = () => api.get("/shops").then(r => setShops(r.data));

  useEffect(() => {
    loadOverview(); loadUsers(); loadProducts(); loadDistricts();
    loadAreas(); loadTerritories(); loadPermissions(); loadAudit();
    loadStockHist(); loadLowStock(); loadRegions(); loadPopups(); loadShops();
  }, []);

  // Phase 12A: Top-level nav groups → sub-tabs
  // Tier 2 (Feb 2026): New "Command Center" group at the top.
  const NAV_GROUPS = {
    command_center: { label: t("nav_command") || "Home", icon: BarChart3, tabs: ["command"] },
    dashboard: { label: t("nav_dashboard"), icon: BarChart3, tabs: ["overview"] },
    orders:    { label: t("nav_orders"),    icon: Package,   tabs: ["reports"] },
    people:    { label: t("nav_people"),    icon: Users,     tabs: ["team", "shops", "permissions"] },
    products:  { label: t("nav_products"),  icon: Package,   tabs: ["products", "popups"] },
    stock:     { label: t("nav_stock"),     icon: Boxes,     tabs: ["stock"] },
    more:      { label: t("nav_more"),      icon: Layers,    tabs: ["districts", "regions", "areas", "territories", "bulk", "audit", "autodelete", "danger"] },
  };
  const TAB_LABELS = {
    command:  { label: t("nav_command") || "Home", icon: BarChart3 },
    overview: { label: "Overview", icon: BarChart3 },
    users:    { label: "All Users", icon: Users },
    team:     { label: "Internal Team", icon: Shield },
    products: { label: t("products"), icon: Package },
    autodelete: { label: "Monthly Auto-Delete", icon: Database },
    districts:{ label: t("districts"), icon: MapPin },
    regions:  { label: "Regions", icon: Globe },
    areas:    { label: "Areas", icon: Map },
    territories: { label: "Territories", icon: Layers },
    stock:    { label: "Stock", icon: Boxes },
    popups:   { label: "Popups", icon: Megaphone },
    shops:    { label: t("outlets"), icon: Store },
    danger:   { label: t("danger_zone"), icon: AlertOctagon },
    bulk:     { label: t("bulk_upload"), icon: Upload },
    permissions: { label: "Permissions", icon: Lock },
    reports:  { label: "Reports", icon: Download },
    audit:    { label: t("audit_logs"), icon: History },
  };
  const groupForTab = (id) => {
    for (const [g, info] of Object.entries(NAV_GROUPS)) if (info.tabs.includes(id)) return g;
    return "dashboard";
  };
  const [topGroup, setTopGroup] = useState(groupForTab(tab));
  // Whenever sub-tab changes externally, sync group
  useEffect(() => { setTopGroup(groupForTab(tab)); /* eslint-disable-next-line */ }, [tab]);
  const subTabs = NAV_GROUPS[topGroup].tabs;

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10">
      <TopBar title="Super Admin" />
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4 fade-up">
        {/* Top-level nav groups */}
        <div className="glass rounded-2xl p-2 inline-flex flex-wrap gap-1 soft-shadow">
          {Object.entries(NAV_GROUPS).map(([gid, info]) => {
            const Icon = info.icon;
            const active = topGroup === gid;
            return (
              <button
                key={gid}
                data-testid={`top-${gid}`}
                onClick={() => { setTopGroup(gid); setTab(info.tabs[0]); }}
                className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-1.5 transition ${active ? "bg-[#0055FF] text-white shadow" : "text-[#1D1D1F] hover:bg-white"}`}
              >
                <Icon className="w-4 h-4" /> {info.label}
              </button>
            );
          })}
        </div>
        {/* Sub-tabs of selected group (only when more than 1) */}
        {subTabs.length > 1 && (
          <div className="flex flex-wrap gap-1 px-2">
            {subTabs.map(stid => {
              const info = TAB_LABELS[stid];
              const Icon = info.icon;
              return (
                <TabButton key={stid} active={tab === stid} onClick={() => setTab(stid)} testid={`tab-${stid}`}>
                  <Icon className="w-4 h-4 inline mr-1" /> {info.label}
                </TabButton>
              );
            })}
          </div>
        )}

        {tab === "command" && (
          <CommandCenter onJumpTab={({ topGroup: g, tab: tt }) => { setTopGroup(g); setTab(tt); }} />
        )}

        {tab === "overview" && overview && (
          <>
            <MissingTamilBanner />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat icon={Users} color="bg-[#0055FF]" label="Users" value={overview.counts.users} testid="ov-users"
                onClick={() => { setTopGroup("people"); setTab("users"); }} hint="Tap to manage" />
              <Stat icon={MapPin} color="bg-[#34C759]" label="Outlets" value={overview.counts.shops} testid="ov-shops"
                onClick={() => { setTopGroup("people"); setTab("shops"); }} hint="Retailers + Wholesalers" />
              <Stat icon={Package} color="bg-[#FF9500]" label="Products" value={overview.counts.products} testid="ov-products"
                onClick={() => { setTopGroup("products"); setTab("products"); }} hint="Manage catalog" />
              <Stat icon={MapPin} color="bg-[#5856D6]" label="Districts" value={overview.counts.districts} testid="ov-districts"
                onClick={() => { setTopGroup("more"); setTab("districts"); }} hint="Hierarchy" />
              <Stat icon={Package} color="bg-[#0055FF]" label="Total Orders" value={overview.counts.orders} testid="ov-orders"
                onClick={() => setDrilldown({ title: "Orders → Units Sold", defaultGroupBy: "product" })} hint="Lifetime" />
              <Stat icon={Truck} color="bg-[#5856D6]" label={t("assigned")} value={overview.today.assigned || 0} testid="ov-assigned"
                onClick={() => setDrilldown({ title: "Assigned Today → Units", defaultGroupBy: "salesman" })} hint="Today" />
              <Stat icon={Check} color="bg-[#34C759]" label="Delivered Today" value={overview.today.delivered || 0} testid="ov-delivered"
                onClick={() => setDrilldown({ title: "Delivered Today → Units", defaultGroupBy: "product" })} hint="Tap for breakdown" />
              <Stat icon={Boxes} color="bg-[#FF9500]" label={t("units_sold")} value={overview.today.units_sold || 0} testid="ov-units"
                onClick={() => setDrilldown({ title: "Units Sold Today", defaultGroupBy: "product" })} hint="Drilldown" />
            </div>

            {/* Quick analytics row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <button
                onClick={() => setDrilldown({ title: "Units by Product", defaultGroupBy: "product" })}
                data-testid="quick-units-product"
                className="bg-gradient-to-br from-[#0055FF] to-[#0A1A3F] text-white rounded-2xl p-4 text-left hover:shadow-xl hover:-translate-y-0.5 transition active:scale-[0.99]"
              >
                <Boxes className="w-5 h-5 mb-2" />
                <div className="text-xs uppercase tracking-wider opacity-80">Drilldown</div>
                <div className="heading-font font-bold text-sm">By Product</div>
              </button>
              <button
                onClick={() => setDrilldown({ title: "Units by District", defaultGroupBy: "district" })}
                data-testid="quick-units-district"
                className="bg-gradient-to-br from-[#5856D6] to-[#3F3D9C] text-white rounded-2xl p-4 text-left hover:shadow-xl hover:-translate-y-0.5 transition active:scale-[0.99]"
              >
                <MapPin className="w-5 h-5 mb-2" />
                <div className="text-xs uppercase tracking-wider opacity-80">Drilldown</div>
                <div className="heading-font font-bold text-sm">By District</div>
              </button>
              <button
                onClick={() => setDrilldown({ title: "Units by Salesman", defaultGroupBy: "salesman" })}
                data-testid="quick-units-salesman"
                className="bg-gradient-to-br from-[#FF9500] to-[#CC7700] text-white rounded-2xl p-4 text-left hover:shadow-xl hover:-translate-y-0.5 transition active:scale-[0.99]"
              >
                <Truck className="w-5 h-5 mb-2" />
                <div className="text-xs uppercase tracking-wider opacity-80">Drilldown</div>
                <div className="heading-font font-bold text-sm">By Salesman</div>
              </button>
              <button
                onClick={() => setDrilldown({ title: "Units by Manager", defaultGroupBy: "manager" })}
                data-testid="quick-units-manager"
                className="bg-gradient-to-br from-[#34C759] to-[#1E8E3E] text-white rounded-2xl p-4 text-left hover:shadow-xl hover:-translate-y-0.5 transition active:scale-[0.99]"
              >
                <Users className="w-5 h-5 mb-2" />
                <div className="text-xs uppercase tracking-wider opacity-80">Drilldown</div>
                <div className="heading-font font-bold text-sm">By Manager</div>
              </button>
            </div>

            {/* APK download (Super Admin only) — Phase G follow-up Feb 2026 */}
            <div className="bg-gradient-to-r from-[#0A1A3F] to-[#0055FF] text-white rounded-2xl p-5 flex items-center gap-4" data-testid="apk-download-card">
              <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0">
                <Download className="w-6 h-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">Android APK</div>
                <div className="heading-font font-bold text-base">Download Capacitor project archive</div>
                <div className="text-xs opacity-85 mt-1">
                  After download, extract → `cd gtroyal-android` → `./gradlew assembleDebug` (needs JDK 17 + Android SDK on x86_64). APK at <code className="bg-white/15 px-1 rounded">app/build/outputs/apk/debug/app-debug.apk</code>
                </div>
              </div>
              <button
                onClick={async () => {
                  try {
                    const res = await api.get("/admin/android-apk-project", { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([res.data], { type: "application/gzip" }));
                    const a = document.createElement("a");
                    a.href = url; a.download = "gtroyal-android.tar.gz";
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("Downloaded — see steps below");
                  } catch (e) {
                    toast.error("Download failed: " + (e?.response?.data?.detail || e.message || "?"));
                  }
                }}
                data-testid="apk-download-btn"
                className="bg-white text-[#0A1A3F] hover:bg-gray-100 rounded-xl px-5 py-3 font-bold text-sm flex items-center gap-2 shrink-0 transition"
              >
                <Download className="w-4 h-4" /> Download
              </button>
            </div>
          </>
        )}

        <UnitsDrilldown
          open={!!drilldown}
          onClose={() => setDrilldown(null)}
          title={drilldown?.title || "Units Breakdown"}
          defaultGroupBy={drilldown?.defaultGroupBy || "product"}
          filters={drilldown?.filters || {}}
        />

        {tab === "team" && (
          <UsersTab users={users.filter(u => !["retailer", "wholesaler", "shopkeeper"].includes(u.role))} districts={districts} onChange={loadUsers} t={t} kind="team" />
        )}

        {tab === "users" && (
          <UsersTab users={users} districts={districts} onChange={loadUsers} t={t} />
        )}

        {tab === "products" && (
          <ProductsTab products={products} onChange={loadProducts} t={t} />
        )}

        {tab === "districts" && (
          <DistrictsTab districts={districts} regions={regions} onChange={loadDistricts} t={t} />
        )}

        {tab === "regions" && (
          <RegionsTab regions={regions} onChange={() => { loadRegions(); loadDistricts(); }} t={t} />
        )}

        {tab === "stock" && (
          <DepotStockTab t={t} />
        )}

        {tab === "popups" && (
          <PopupsTab popups={popups} districts={districts} onChange={loadPopups} t={t} />
        )}

        {tab === "shops" && (
          <ShopsTab shops={shops} districts={districts} onChange={loadShops} t={t} />
        )}

        {tab === "danger" && (
          <DangerZoneTab t={t} districts={districts} onChange={() => { loadUsers(); loadOverview(); }} />
        )}

        {tab === "autodelete" && (
          <AutoDeleteTab t={t} />
        )}

        {tab === "bulk" && (
          <BulkUploadTab t={t} />
        )}

        {tab === "areas" && (
          <AreasTab areas={areas} districts={districts} onChange={loadAreas} t={t} />
        )}

        {tab === "territories" && (
          <TerritoriesTab territories={territories} areas={areas} onChange={loadTerritories} t={t} />
        )}

        {tab === "permissions" && (
          <PermissionsTab permissions={permissions} onChange={loadPermissions} t={t} />
        )}

        {tab === "reports" && (
          <ReportsTab t={t} />
        )}

        {tab === "audit" && (
          <AuditTab logs={auditLogs} onRefresh={loadAudit} t={t} />
        )}
      </div>
    </div>
  );
}

const UsersTab = ({ users, districts, onChange, t, kind = "all" }) => {
  const { lang } = useLang();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [blocking, setBlocking] = useState(null); // user being blocked (modal)
  const [blockReason, setBlockReason] = useState("");
  const initialRole = kind === "team" ? "salesman" : "retailer";
  const [form, setForm] = useState({ name: "", name_ta: "", phone: "", role: initialRole, pin: "1234", district_id: "", shop_name: "", shop_name_ta: "", address: "" });
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active"); // active | blocked | deleted | all
  const [search, setSearch] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  // Tier 2.1 (Feb 2026): Tamil-aware district map
  const dmap = Object.fromEntries((districts || []).map(d => [d.id, (lang === "ta" && d.name_ta) ? d.name_ta : d.name]));

  const submit = async () => {
    if (!form.name_ta?.trim()) return toast.error(t("tamil_name_required") || "Tamil name is required");
    if ((form.role === "retailer" || form.role === "wholesaler") && !form.shop_name_ta?.trim()) {
      return toast.error((t("shop_name") || "Shop name") + " — " + (t("tamil_name_required") || "Tamil name is required"));
    }
    try {
      await api.post("/users", form);
      toast.success("User created");
      setShowAdd(false);
      setForm({ name: "", name_ta: "", phone: "", role: initialRole, pin: "1234", district_id: "", shop_name: "", shop_name_ta: "", address: "" });
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const submitEdit = async () => {
    try {
      const payload = {
        name: editing.name,
        name_ta: editing.name_ta,
        phone: editing.phone,
        district_id: editing.district_id || null,
      };
      if (editing.pin) payload.pin = editing.pin;
      if (editing.shop_name !== undefined) payload.shop_name = editing.shop_name;
      if (editing.shop_name_ta !== undefined) payload.shop_name_ta = editing.shop_name_ta;
      if (editing.address !== undefined) payload.address = editing.address;
      await api.put(`/users/${editing.id}`, payload);
      toast.success("User updated");
      setEditing(null);
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete") + "\n" + t("soft_delete"))) return;
    await api.delete(`/users/${id}`);
    toast.success(t("deleted")); onChange();
  };

  const restore = async (id) => {
    await api.post(`/users/${id}/restore`);
    toast.success(t("restore")); onChange();
  };

  const block = async () => {
    if (!blockReason.trim()) return toast.error(t("block_reason"));
    try {
      await api.post(`/users/${blocking.id}/block`, { reason: blockReason.trim() });
      toast.success(t("blocked"));
      setBlocking(null); setBlockReason("");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const unblock = async (id) => {
    await api.post(`/users/${id}/unblock`);
    toast.success(t("unblock_user"));
    onChange();
  };

  const filtered = users.filter(u => {
    if (filter !== "all" && u.role !== filter) return false;
    if (districtFilter && u.district_id !== districtFilter) return false;
    if (statusFilter === "active" && (u.blocked || u.deleted_at || !u.active)) return false;
    if (statusFilter === "blocked" && !u.blocked) return false;
    if (statusFilter === "deleted" && !u.deleted_at) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(u.name?.toLowerCase().includes(q) || u.name_ta?.toLowerCase().includes(q) || u.phone?.includes(q))) return false;
    }
    return true;
  });

  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="heading-font font-bold text-xl">{kind === "team" ? "Internal Team" : t("users")}</h2>
          {kind === "team" && <span className="text-xs text-[#86868B]">Super Admin / Management / Manager / Salesman</span>}
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search name or phone…"
            data-testid="user-search"
            className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0055FF]/30 min-w-[180px]"
          />
          <select value={filter} onChange={e => setFilter(e.target.value)} className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm" data-testid="user-filter">
            <option value="all">All roles</option>
            <option value="manager">Managers</option>
            <option value="salesman">Salesmen</option>
            <option value="management">Management</option>
            {kind !== "team" && <option value="retailer">Retailers</option>}
            {kind !== "team" && <option value="wholesaler">Wholesalers</option>}
          </select>
          <select value={districtFilter} onChange={e => setDistrictFilter(e.target.value)} className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm" data-testid="user-district-filter">
            <option value="">All districts</option>
            {districts.map(d => <option key={d.id} value={d.id}>{(lang === "ta" && d.name_ta) ? d.name_ta : d.name}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm" data-testid="user-status-filter">
            <option value="active">{t("user_status_active")}</option>
            <option value="blocked">{t("user_status_blocked")}</option>
            <option value="deleted">{t("user_status_deleted")}</option>
            <option value="all">All</option>
          </select>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-user-btn" className="bg-[#0055FF] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t("add_user")}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50/60">
            <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
              <th className="px-4 py-3">{t("name")}</th>
              <th className="px-4 py-3">{t("phone")}</th>
              <th className="px-4 py-3">{t("role")}</th>
              <th className="px-4 py-3">{t("district")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(u => (
              <tr key={u.id} className={u.deleted_at ? "opacity-50" : ""} data-testid={`user-row-${u.phone}`}>
                <td className="px-4 py-3 font-semibold">
                  {(lang === "ta" && u.name_ta) ? u.name_ta : u.name}
                  {!u.name_ta && u.role !== "super_admin" && <span className="ml-1.5 text-[10px] text-amber-700 font-bold" title="Tamil name missing">⚠ TA</span>}
                  {u.blocked && <span className="ml-2 pill bg-red-50 text-red-700">{t("blocked")}</span>}
                  {u.deleted_at && <span className="ml-2 pill bg-gray-100 text-gray-500">{t("deleted")}</span>}
                </td>
                <td className="px-4 py-3 font-mono">{u.phone}</td>
                <td className="px-4 py-3"><span className="pill bg-blue-50 text-blue-700">{u.role}</span></td>
                <td className="px-4 py-3 text-[#86868B]">{dmap[u.district_id] || "-"}</td>
                <td className="px-4 py-3 text-right">
                  {u.role !== "super_admin" && (
                    <div className="flex items-center justify-end gap-1">
                      {!u.deleted_at && !u.blocked && (
                        <>
                          <button onClick={() => setEditing({ ...u, pin: "" })} className="text-[#0055FF] hover:bg-blue-50 p-2 rounded-lg" data-testid={`edit-user-${u.phone}`} title={t("edit")}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => setBlocking(u)} className="text-orange-600 hover:bg-orange-50 p-2 rounded-lg" data-testid={`block-user-${u.phone}`} title={t("block_user")}>
                            <Lock className="w-4 h-4" />
                          </button>
                          <button onClick={() => remove(u.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" data-testid={`del-user-${u.phone}`} title={t("delete")}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {u.blocked && !u.deleted_at && (
                        <button onClick={() => unblock(u.id)} className="px-3 py-1 rounded-lg bg-[#34C759] text-white text-xs font-bold" data-testid={`unblock-user-${u.phone}`}>
                          {t("unblock_user")}
                        </button>
                      )}
                      {u.deleted_at && (
                        <button onClick={() => restore(u.id)} className="px-3 py-1 rounded-lg bg-[#0055FF] text-white text-xs font-bold" data-testid={`restore-user-${u.phone}`}>
                          {t("restore")}
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {blocking && (
        <Modal onClose={() => { setBlocking(null); setBlockReason(""); }} title={`${t("block_user")}: ${blocking.name}`}>
          <div className="space-y-3">
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-sm text-orange-800">
              {t("blocked_message")}
            </div>
            <Input label={t("block_reason")} value={blockReason} onChange={setBlockReason} testid="block-reason-input" />
            <button onClick={block} data-testid="confirm-block-btn" className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl py-3 font-bold">
              {t("block_user")}
            </button>
          </div>
        </Modal>
      )}

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)} title={t("add_user")}>
          <div className="space-y-3">
            <Input label={t("name") + " (EN)"} value={form.name} onChange={v => setForm({...form, name: v})} testid="form-name" />
            <Input label={t("name") + " (TA) *"} value={form.name_ta} onChange={v => setForm({...form, name_ta: v})} testid="form-name-ta" />
            <Input label={t("phone")} value={form.phone} onChange={v => setForm({...form, phone: v})} testid="form-phone" />
            <Input label="PIN" value={form.pin} onChange={v => setForm({...form, pin: v})} testid="form-pin" />
            <div>
              <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">{t("role")}</label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} data-testid="form-role" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 mt-1">
                <option value="retailer">Retailer (Outlet)</option>
                <option value="wholesaler">Wholesaler (Outlet)</option>
                <option value="salesman">Salesman</option>
                <option value="manager">Manager</option>
                <option value="management">Management</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">{t("district")}</label>
              <select value={form.district_id} onChange={e => setForm({...form, district_id: e.target.value})} data-testid="form-district" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 mt-1">
                <option value="">-</option>
                {districts.map(d => <option key={d.id} value={d.id}>{d.name}{d.name_ta ? ` (${d.name_ta})` : ""}</option>)}
              </select>
            </div>
            {(form.role === "shopkeeper" || form.role === "retailer" || form.role === "wholesaler") && (
              <>
                <Input label={t("shop_name") + " (EN)"} value={form.shop_name} onChange={v => setForm({...form, shop_name: v})} testid="form-shop-name" />
                <Input label={t("shop_name") + " (TA) *"} value={form.shop_name_ta} onChange={v => setForm({...form, shop_name_ta: v})} testid="form-shop-name-ta" />
                <Input label={t("address")} value={form.address} onChange={v => setForm({...form, address: v})} testid="form-address" />
              </>
            )}
            <div className="text-[11px] text-[#86868B]">* {t("tamil_name_required") || "Tamil name is required"}</div>
            <button onClick={submit} data-testid="submit-user" className="w-full bg-[#0055FF] text-white rounded-xl py-3 font-bold">{t("save")}</button>
          </div>
        </Modal>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title="Edit User">
          <div className="space-y-3">
            <Input label={t("name") + " (EN)"} value={editing.name || ""} onChange={v => setEditing({...editing, name: v})} testid="edit-name" />
            <Input label={t("name") + " (TA)"} value={editing.name_ta || ""} onChange={v => setEditing({...editing, name_ta: v})} testid="edit-name-ta" />
            <Input label={t("phone")} value={editing.phone || ""} onChange={v => setEditing({...editing, phone: v})} testid="edit-phone" />
            <Input label="New PIN (leave blank to keep)" value={editing.pin || ""} onChange={v => setEditing({...editing, pin: v})} testid="edit-pin" />
            <div>
              <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">{t("district")}</label>
              <select value={editing.district_id || ""} onChange={e => setEditing({...editing, district_id: e.target.value})} data-testid="edit-district" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 mt-1">
                <option value="">-</option>
                {districts.map(d => <option key={d.id} value={d.id}>{d.name}{d.name_ta ? ` (${d.name_ta})` : ""}</option>)}
              </select>
            </div>
            {(editing.role === "retailer" || editing.role === "wholesaler" || editing.role === "shopkeeper") && (
              <>
                <Input label={t("shop_name") + " (EN)"} value={editing.shop_name || ""} onChange={v => setEditing({...editing, shop_name: v})} testid="edit-shop-name" />
                <Input label={t("shop_name") + " (TA)"} value={editing.shop_name_ta || ""} onChange={v => setEditing({...editing, shop_name_ta: v})} testid="edit-shop-name-ta" />
                <Input label={t("address")} value={editing.address || ""} onChange={v => setEditing({...editing, address: v})} testid="edit-address" />
              </>
            )}
            <button onClick={submitEdit} data-testid="submit-edit-user" className="w-full bg-[#0055FF] text-white rounded-xl py-3 font-bold">{t("save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const ProductsTab = ({ products, onChange, t }) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", name_en: "", name_ta: "", sku: "", price: 0, unit: "pc", image_url: "" });
  const submit = async () => {
    try {
      // Keep name in sync with name_en for back-compat
      const en = (form.name_en || form.name || "").trim();
      const payload = {
        ...form,
        name: en,
        name_en: en,
        name_ta: form.name_ta?.trim() || null,
        // Phase 13: price stays internal-only, default 0 (UI never asks).
        price: 0,
      };
      await api.post("/products", payload);
      toast.success("Product added"); setShow(false);
      setForm({ name: "", name_en: "", name_ta: "", sku: "", price: 0, unit: "pc", image_url: "" });
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/products/${id}`); toast.success("Deleted"); onChange();
  };
  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <h2 className="heading-font font-bold text-xl">{t("products")}</h2>
        <button onClick={() => setShow(true)} data-testid="add-product-btn" className="bg-[#0055FF] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t("add_product")}
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-5">
        {products.map(p => (
          <div key={p.id} className="flex items-center gap-3 p-3 bg-[#F5F5F7] rounded-2xl">
            <img src={imageSrc(p.image_url)} alt="" className="w-14 h-14 rounded-xl object-cover bg-white" />
            <div className="flex-1 min-w-0">
              <div className="font-bold heading-font truncate">
                {p.name_en || p.name}
                {!p.name_ta && <span className="ml-1.5 text-[10px] text-amber-700 font-bold" title="Tamil name missing">⚠ TA</span>}
              </div>
              {p.name_ta && <div className="text-xs text-[#0055FF] truncate">{p.name_ta}</div>}
              <div className="text-xs text-[#86868B]">{p.sku} • {p.unit || "pc"}</div>
            </div>
            <button onClick={() => remove(p.id)} className="text-red-500 p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {show && (
        <Modal onClose={() => setShow(false)} title={t("add_product")}>
          <div className="space-y-3">
            <BilingualNameInputs
              valueEn={form.name_en}
              valueTa={form.name_ta}
              onChangeEn={v => setForm({...form, name_en: v, name: v})}
              onChangeTa={v => setForm({...form, name_ta: v})}
              testid="prod"
            />
            <ProductImageUpload value={form.image_url} onChange={v => setForm({...form, image_url: v})} testid="prod-img" />
            <Input label={t("sku")} value={form.sku} onChange={v => setForm({...form, sku: v})} testid="prod-sku" />
            <Input label="Unit" value={form.unit} onChange={v => setForm({...form, unit: v})} testid="prod-unit" />
            <button onClick={submit} data-testid="submit-product" className="w-full bg-[#0055FF] text-white rounded-xl py-3 font-bold">{t("save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const DistrictsTab = ({ districts, regions, onChange, t }) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", name_ta: "", region: "Tamil Nadu", region_id: "" });
  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Name (EN) required");
    if (!form.name_ta?.trim()) return toast.error(t("tamil_name_required") || "Tamil name is required");
    await api.post("/districts", form); toast.success("Added"); setShow(false); setForm({ name: "", name_ta: "", region: "Tamil Nadu", region_id: "" }); onChange();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/districts/${id}`); toast.success("Deleted"); onChange();
  };
  const rmap = Object.fromEntries((regions || []).map(r => [r.id, r.name]));
  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <h2 className="heading-font font-bold text-xl">{t("districts")}</h2>
        <button onClick={() => setShow(true)} data-testid="add-district-btn" className="bg-[#0055FF] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t("add_district")}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-5">
        {districts.map(d => (
          <div key={d.id} className="flex items-center gap-3 p-4 bg-[#F5F5F7] rounded-2xl">
            <MapPin className="w-5 h-5 text-[#0055FF]" />
            <div className="flex-1">
              <div className="font-bold heading-font">{d.name}</div>
              <div className="text-xs text-[#86868B] flex items-center gap-1">
                {d.name_ta ? <span className="text-[#0055FF] font-semibold">{d.name_ta}</span> : <span className="text-amber-700 font-semibold">⚠ {t("tamil_name_missing") || "Tamil name missing"}</span>}
                <span>•</span>
                <span>{rmap[d.region_id] || d.region}</span>
              </div>
            </div>
            <button onClick={() => remove(d.id)} className="text-red-500 p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {show && (
        <Modal onClose={() => setShow(false)} title={t("add_district")}>
          <div className="space-y-3">
            <Input label={t("name") + " (EN)"} value={form.name} onChange={v => setForm({...form, name: v})} testid="dist-name" />
            <Input label={t("name") + " (TA) *"} value={form.name_ta} onChange={v => setForm({...form, name_ta: v})} testid="dist-name-ta" />
            <div>
              <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">Region</label>
              <select value={form.region_id} onChange={e => setForm({...form, region_id: e.target.value})} data-testid="dist-region-select" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 mt-1">
                <option value="">- Select Region (or use text below) -</option>
                {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <Input label="Region (text fallback)" value={form.region} onChange={v => setForm({...form, region: v})} testid="dist-region" />
            <div className="text-[11px] text-[#86868B]">* {t("tamil_name_required") || "Tamil name is required"}</div>
            <button onClick={submit} data-testid="submit-district" className="w-full bg-[#0055FF] text-white rounded-xl py-3 font-bold">{t("save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const BulkUploadTab = ({ t }) => {
  // see below — actual component
  return <BulkUploadTabImpl t={t} />;
};

const AutoDeleteTab = ({ t }) => {
  const [cfg, setCfg] = useState({ enabled: false, day_of_month: 1, last_run_at: null, last_backup_id: null });
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/admin/auto-delete/settings"),
      api.get("/admin/auto-delete/backups"),
    ]);
    setCfg(a.data); setBackups(b.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setBusy(true);
    try {
      await api.put("/admin/auto-delete/settings", next);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const runNow = async () => {
    if (!window.confirm("Build a backup AND archive all current order history? Super Admin can still view archived data later.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/auto-delete/run-now");
      toast.success(`Archived ${r.data.purged} orders. Backup saved.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const downloadBackup = (bid) => {
    const token = localStorage.getItem("xtra_token");
    fetch(`${API}/admin/auto-delete/backup/${bid}.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `gtroyal-backup-${bid.slice(0,8)}.csv`;
        link.click();
        toast.success("Downloaded");
      })
      .catch(() => toast.error("Download failed"));
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl soft-shadow p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="heading-font font-bold text-xl">Monthly Auto-Delete</h2>
            <p className="text-sm text-[#86868B] mt-1">
              Automatically clears order/sales history every month for managers, salesmen, retailers and wholesalers — but always builds a Super Admin backup first.
            </p>
          </div>
          <div className={`pill ${cfg.enabled ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`} data-testid="autodelete-status">
            {cfg.enabled ? "ENABLED" : "DISABLED"}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-5">
          <label className="bg-[#F5F5F7] rounded-xl p-4 flex items-center justify-between cursor-pointer">
            <div>
              <div className="text-xs text-[#86868B] uppercase tracking-wider font-bold">Enable</div>
              <div className="text-sm">Run automatically every month</div>
            </div>
            <input type="checkbox" data-testid="autodelete-enable"
              checked={cfg.enabled}
              onChange={(e) => save({ enabled: e.target.checked, day_of_month: cfg.day_of_month })}
              className="w-5 h-5"
            />
          </label>
          <div className="bg-[#F5F5F7] rounded-xl p-4">
            <div className="text-xs text-[#86868B] uppercase tracking-wider font-bold">Day of Month</div>
            <select
              data-testid="autodelete-day"
              value={cfg.day_of_month}
              onChange={(e) => save({ enabled: cfg.enabled, day_of_month: parseInt(e.target.value, 10) })}
              className="mt-1 w-full bg-white rounded-lg px-3 py-2 text-sm font-bold"
            >
              {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                <option key={d} value={d}>Day {d}</option>
              ))}
            </select>
          </div>
          <div className="bg-[#F5F5F7] rounded-xl p-4">
            <div className="text-xs text-[#86868B] uppercase tracking-wider font-bold">Last Run</div>
            <div className="mt-1 text-sm">{cfg.last_run_at ? new Date(cfg.last_run_at).toLocaleString() : "Never"}</div>
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={runNow} disabled={busy} data-testid="autodelete-run-now" className="bg-[#FF3B30] hover:bg-[#CC2D24] disabled:opacity-50 text-white rounded-xl px-4 py-2.5 font-bold text-sm flex items-center gap-2">
            <RotateCcw className="w-4 h-4" /> Run Now (Backup + Archive)
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h3 className="heading-font font-bold text-lg">Backup History</h3>
          <p className="text-xs text-[#86868B] mt-0.5">Each backup snapshots all orders + audit logs. Download anytime.</p>
        </div>
        {backups.length === 0 ? (
          <div className="p-8 text-center text-[#86868B]" data-testid="no-backups">No backups yet</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {backups.map(b => (
              <div key={b.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`backup-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-bold text-sm">Backup {b.id.slice(0, 8)}</div>
                  <div className="text-xs text-[#86868B]">{new Date(b.created_at).toLocaleString()} • {b.orders_count} orders • {b.audit_count} audit</div>
                </div>
                <button onClick={() => downloadBackup(b.id)} className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl px-3 py-2 font-bold text-xs flex items-center gap-1.5" data-testid={`backup-dl-${b.id}`}>
                  <Download className="w-3.5 h-3.5" /> CSV
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BulkUploadTabImpl = ({ t }) => {
  const kinds = [
    { key: "shopkeepers", label: "Outlets (Retailers + Wholesalers)", hint: "name, phone, pin, district_name, shop_name, address, shop_type (retailer|wholesaler — default: retailer)" },
    { key: "salesmen", label: "Salesmen", hint: "name, phone, pin, district_name" },
    { key: "managers", label: "Managers", hint: "name, phone, pin, district_name" },
    { key: "products", label: "Products", hint: "name, sku, price, unit, image_url" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {kinds.map(k => <BulkCard key={k.key} kind={k.key} label={k.label} hint={k.hint} t={t} />)}
    </div>
  );
};

const BulkCard = ({ kind, label, hint, t }) => {
  const ref = useRef();
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post(`/bulk-upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`${r.data.inserted} added`);
    } catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl p-5 soft-shadow">
      <div className="flex items-center gap-2 mb-2">
        <FileSpreadsheet className="w-5 h-5 text-[#0055FF]" />
        <h3 className="heading-font font-bold text-lg">{label}</h3>
      </div>
      <div className="text-xs text-[#86868B] mb-4">CSV columns: {hint}</div>
      <input ref={ref} type="file" accept=".csv" hidden onChange={upload} data-testid={`bulk-input-${kind}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`bulk-btn-${kind}`}
        className="w-full bg-[#0055FF] hover:bg-[#0044CC] disabled:opacity-50 text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2">
        <Upload className="w-4 h-4" /> {busy ? "Uploading…" : t("upload_csv")}
      </button>
      {result && (
        <div className="mt-3 text-xs space-y-1">
          <div className="text-green-700">✓ Inserted: {result.inserted}</div>
          {result.skipped > 0 && <div className="text-orange-700">⚠ Skipped: {result.skipped}</div>}
          {result.errors?.slice(0, 3).map((e, i) => <div key={i} className="text-red-600">• {e}</div>)}
        </div>
      )}
    </div>
  );
};

const Modal = ({ onClose, title, children }) => (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
    <div className="bg-white rounded-3xl p-6 max-w-md w-full soft-shadow-md fade-up" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="heading-font font-bold text-xl">{title}</h3>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const Input = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">{label}</label>
    <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
      className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 mt-1 outline-none focus:ring-2 focus:ring-[#0055FF]/30" />
  </div>
);

// Phase 14: Product image upload (file picker + URL fallback). Uses /api/upload (object storage).
const ProductImageUpload = ({ value, onChange, testid = "prod-img" }) => {
  const [busy, setBusy] = React.useState(false);
  const [mode, setMode] = React.useState(value ? "preview" : "pick");
  const inputRef = React.useRef();

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Store the storage_path (NOT a fully-qualified URL) so each viewer can build
      // an authenticated URL with their OWN token via fileUrl(). External http(s) URLs
      // are also supported (just paste in the URL field).
      const path = r.data?.path;
      if (!path) throw new Error("Upload response missing path");
      onChange(path);
      setMode("preview");
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">Product Image</label>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={onPick} data-testid={`${testid}-file`} />
      {value && mode === "preview" ? (
        <div className="mt-1 flex items-center gap-3 p-2 bg-[#F5F5F7] rounded-xl">
          <img src={imageSrc(value)} alt="" className="w-16 h-16 rounded-xl object-cover bg-white" data-testid={`${testid}-preview`} />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-[#86868B] truncate">{value}</div>
            <div className="flex gap-2 mt-1">
              <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="px-3 py-1.5 rounded-lg bg-[#0055FF] text-white text-xs font-bold disabled:opacity-50" data-testid={`${testid}-replace`}>
                {busy ? "Uploading…" : "Replace"}
              </button>
              <button type="button" onClick={() => { onChange(""); setMode("pick"); }} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-bold" data-testid={`${testid}-clear`}>Remove</button>
              <button type="button" onClick={() => setMode("url")} className="px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-xs font-bold">Edit URL</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="w-full bg-[#F5F5F7] hover:bg-gray-100 border-2 border-dashed border-[#0055FF]/40 rounded-xl py-6 text-sm font-bold text-[#0055FF] disabled:opacity-50 flex flex-col items-center gap-1" data-testid={`${testid}-pick`}>
            <Upload className="w-5 h-5" />
            {busy ? "Uploading…" : "Upload product image"}
            <span className="text-[10px] font-normal text-[#86868B]">PNG / JPG / WebP up to 5MB</span>
          </button>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            data-testid={`${testid}-url`}
            placeholder="…or paste an image URL"
            className="w-full bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0055FF]/30 outline-none"
          />
        </div>
      )}
    </div>
  );
};

// Phase 12C: Bilingual name inputs (EN + TA) with one-tap auto-translate via Gemini 3 Flash.
// Usage: <BilingualNameInputs valueEn={form.name_en} valueTa={form.name_ta}
//   onChangeEn={v => setForm({...form, name_en: v, name: v})}
//   onChangeTa={v => setForm({...form, name_ta: v})} testid="prod" />
const BilingualNameInputs = ({ valueEn, valueTa, onChangeEn, onChangeTa, testid = "bilingual" }) => {
  const [translating, setTranslating] = React.useState(false);
  const autoTranslate = async () => {
    if (!valueEn || !valueEn.trim()) {
      toast.error("Enter English name first");
      return;
    }
    setTranslating(true);
    try {
      const r = await api.post("/admin/translate", { text: valueEn.trim(), target_lang: "ta" });
      onChangeTa(r.data?.translated || "");
      toast.success("Translated to Tamil");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
    } finally {
      setTranslating(false);
    }
  };
  return (
    <div className="space-y-3">
      <Input label="Name (English)" value={valueEn || ""} onChange={onChangeEn} testid={`${testid}-name-en`} />
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <Input label="Name (Tamil)" value={valueTa || ""} onChange={onChangeTa} testid={`${testid}-name-ta`} />
        </div>
        <button type="button" onClick={autoTranslate} disabled={translating || !valueEn}
          data-testid={`${testid}-auto-translate-btn`}
          className="bg-[#34C759] disabled:opacity-50 text-white rounded-xl px-3 py-3 text-xs font-bold whitespace-nowrap flex items-center gap-1">
          <Globe className="w-4 h-4" /> {translating ? "Translating…" : "Auto translate"}
        </button>
      </div>
    </div>
  );
};

const AreasTab = ({ areas, districts, onChange, t }) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", name_en: "", name_ta: "", district_id: "" });
  const dmap = Object.fromEntries(districts.map(d => [d.id, d.name]));
  const submit = async () => {
    if ((!form.name && !form.name_en) || !form.district_id) return toast.error("Fill all fields");
    const en = (form.name_en || form.name || "").trim();
    await api.post("/areas", { name: en, name_en: en, name_ta: form.name_ta?.trim() || null, district_id: form.district_id });
    toast.success("Added"); setShow(false); setForm({ name: "", name_en: "", name_ta: "", district_id: "" }); onChange();
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/areas/${id}`); toast.success("Deleted"); onChange(); };
  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <h2 className="heading-font font-bold text-xl">Areas</h2>
        <button onClick={() => setShow(true)} data-testid="add-area-btn" className="bg-[#0055FF] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Area
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-5">
        {areas.map(a => (
          <div key={a.id} className="flex items-center gap-3 p-4 bg-[#F5F5F7] rounded-2xl">
            <Map className="w-5 h-5 text-[#0055FF]" />
            <div className="flex-1">
              <div className="font-bold heading-font">{a.name_en || a.name}</div>
              {a.name_ta && <div className="text-xs text-[#0055FF]">{a.name_ta}</div>}
              <div className="text-xs text-[#86868B]">{dmap[a.district_id] || "-"}</div>
            </div>
            <button onClick={() => remove(a.id)} className="text-red-500 p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {areas.length === 0 && <div className="col-span-full text-center text-[#86868B] py-8">No areas yet</div>}
      </div>
      {show && (
        <Modal onClose={() => setShow(false)} title="Add Area">
          <div className="space-y-3">
            <BilingualNameInputs
              valueEn={form.name_en}
              valueTa={form.name_ta}
              onChangeEn={v => setForm({...form, name_en: v, name: v})}
              onChangeTa={v => setForm({...form, name_ta: v})}
              testid="area"
            />
            <div>
              <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">{t("district")}</label>
              <select value={form.district_id} onChange={e => setForm({...form, district_id: e.target.value})} data-testid="area-district" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 mt-1">
                <option value="">Select district</option>
                {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <button onClick={submit} data-testid="submit-area" className="w-full bg-[#0055FF] text-white rounded-xl py-3 font-bold">{t("save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const TerritoriesTab = ({ territories, areas, onChange, t }) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", area_id: "" });
  const amap = Object.fromEntries(areas.map(a => [a.id, a.name]));
  const submit = async () => {
    if (!form.name || !form.area_id) return toast.error("Fill all fields");
    await api.post("/territories", form); toast.success("Added"); setShow(false); setForm({ name: "", area_id: "" }); onChange();
  };
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/territories/${id}`); toast.success("Deleted"); onChange(); };
  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <h2 className="heading-font font-bold text-xl">Territories</h2>
        <button onClick={() => setShow(true)} data-testid="add-territory-btn" disabled={areas.length === 0} className="bg-[#0055FF] disabled:opacity-50 text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Territory
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-5">
        {territories.map(tr => (
          <div key={tr.id} className="flex items-center gap-3 p-4 bg-[#F5F5F7] rounded-2xl">
            <Layers className="w-5 h-5 text-[#0055FF]" />
            <div className="flex-1">
              <div className="font-bold heading-font">{tr.name}</div>
              <div className="text-xs text-[#86868B]">{amap[tr.area_id] || "-"}</div>
            </div>
            <button onClick={() => remove(tr.id)} className="text-red-500 p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {territories.length === 0 && <div className="col-span-full text-center text-[#86868B] py-8">No territories yet. Create areas first.</div>}
      </div>
      {show && (
        <Modal onClose={() => setShow(false)} title="Add Territory">
          <div className="space-y-3">
            <Input label="Territory Name" value={form.name} onChange={v => setForm({...form, name: v})} testid="territory-name" />
            <div>
              <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">Area</label>
              <select value={form.area_id} onChange={e => setForm({...form, area_id: e.target.value})} data-testid="territory-area" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 mt-1">
                <option value="">Select area</option>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <button onClick={submit} data-testid="submit-territory" className="w-full bg-[#0055FF] text-white rounded-xl py-3 font-bold">{t("save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const PermissionsTab = ({ permissions, onChange, t }) => {
  const [draft, setDraft] = useState(permissions);
  useEffect(() => { setDraft(permissions); }, [permissions]);
  const toggle = (role, perm) => {
    setDraft(prev => ({ ...prev, [role]: { ...prev[role], [perm]: !prev[role][perm] } }));
  };
  const save = async (role) => {
    try {
      await api.put("/permissions", { role, permissions: draft[role] });
      toast.success(`${role} permissions saved`); onChange();
    } catch (e) { toast.error("Error"); }
  };
  return (
    <div className="space-y-5">
      {Object.entries(draft).map(([role, perms]) => (
        <div key={role} className="bg-white rounded-2xl soft-shadow p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="heading-font font-bold text-lg capitalize">{role.replace("_", " ")}</h3>
              <p className="text-xs text-[#86868B]">Toggle capabilities for this role</p>
            </div>
            <button onClick={() => save(role)} data-testid={`save-perm-${role}`} className="bg-[#0055FF] text-white rounded-xl px-4 py-2 font-bold text-sm">{t("save")}</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(perms).map(([perm, val]) => (
              <label key={perm} className="flex items-center justify-between p-3 bg-[#F5F5F7] rounded-xl cursor-pointer hover:bg-blue-50 transition">
                <span className="text-sm font-semibold text-[#1D1D1F]">{perm.replace(/_/g, " ")}</span>
                <button
                  onClick={() => toggle(role, perm)}
                  data-testid={`perm-${role}-${perm}`}
                  className={`relative w-11 h-6 rounded-full transition ${val ? "bg-[#34C759]" : "bg-gray-300"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${val ? "translate-x-5" : ""}`} />
                </button>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const ReportsTab = ({ t }) => {
  const download = (path, filename) => {
    const token = localStorage.getItem("xtra_token");
    const url = `${API}${path}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
      })
      .catch(() => toast.error("Download failed"));
  };
  // Tier 2.1 (Feb 2026): Open print-ready HTML (with embedded Print/Save-as-PDF button) in new tab.
  const openPrint = async (path) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const w = window.open(url, "_blank");
      if (!w) toast.error("Pop-up blocked");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not open print view");
    }
  };
  const reports = [
    { key: "orders", title: "All Orders", desc: "Full order log with shop, salesman, status, items, units (NO money)", path: "/reports/orders.csv", file: "orders.csv", printPath: "/reports/orders.html" },
    { key: "units", title: "Units Summary", desc: "Last 30 days daily delivered orders + units (UNIT-ONLY)", path: "/reports/units-summary.csv", file: "units-summary.csv" },
    { key: "units-by-product", title: "Units by Product", desc: "Per-product units delivered breakdown", path: "/reports/units-by-product.csv", file: "units-by-product.csv" },
    { key: "stock", title: "Stock Snapshot", desc: "Depot balance + on-road stock + recent receipts (Print + PDF)", printPath: "/reports/stock.html", printOnly: true },
    { key: "attendance", title: "Attendance", desc: "GPS-based attendance with Excel + Print/PDF", path: null, attendanceLink: "/management/attendance-report" },
  ];
  const nav = useNavigate();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {reports.map(r => (
        <div key={r.key} className="bg-white rounded-2xl p-5 soft-shadow flex flex-col">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-[#0055FF]/10 grid place-items-center">
              <FileSpreadsheet className="w-6 h-6 text-[#0055FF]" />
            </div>
            <div>
              <h3 className="heading-font font-bold text-lg">{r.title}</h3>
              <p className="text-xs text-[#86868B]">{r.desc}</p>
            </div>
          </div>
          <div className="mt-auto pt-3 grid grid-cols-2 gap-2">
            {r.attendanceLink ? (
              <button
                onClick={() => nav(r.attendanceLink)}
                data-testid={`open-${r.key}`}
                className="col-span-2 bg-[#34C759] hover:bg-[#28A745] text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2"
              >
                <FileSpreadsheet className="w-4 h-4" /> Open Attendance Center
              </button>
            ) : (
              <>
                {r.path && (
                  <button
                    onClick={() => download(r.path, r.file)}
                    data-testid={`download-${r.key}`}
                    className={`bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl py-3 font-bold flex items-center justify-center gap-2 ${r.printOnly ? "hidden" : ""}`}
                  >
                    <Download className="w-4 h-4" /> CSV
                  </button>
                )}
                {r.printPath && (
                  <button
                    onClick={() => openPrint(r.printPath)}
                    data-testid={`print-${r.key}`}
                    className={`bg-white hover:bg-[#0055FF] hover:text-white text-[#0055FF] border border-[#0055FF] rounded-xl py-3 font-bold flex items-center justify-center gap-2 ${r.printOnly ? "col-span-2" : ""}`}
                  >
                    <Printer className="w-4 h-4" /> Print / PDF
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const AuditTab = ({ logs, onRefresh, t }) => {
  const actionColor = {
    create_user: "bg-green-50 text-green-700",
    update_user: "bg-blue-50 text-blue-700",
    disable_user: "bg-rose-50 text-rose-700",
    create_product: "bg-green-50 text-green-700",
    update_product: "bg-blue-50 text-blue-700",
    delete_product: "bg-rose-50 text-rose-700",
    create_district: "bg-green-50 text-green-700",
    delete_district: "bg-rose-50 text-rose-700",
    update_permissions: "bg-violet-50 text-violet-700",
    verify_upi: "bg-amber-50 text-amber-700",
  };
  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <h2 className="heading-font font-bold text-xl flex items-center gap-2"><History className="w-5 h-5 text-[#0055FF]" /> {t("audit_logs")}</h2>
        <button onClick={onRefresh} data-testid="refresh-audit" className="text-sm text-[#0055FF] font-bold">↻ Refresh</button>
      </div>
      {logs.length === 0 ? <div className="p-8 text-center text-[#86868B]">No audit entries</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60">
              <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Actor</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map(l => (
                <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                  <td className="px-4 py-3 text-xs text-[#86868B] whitespace-nowrap">{new Date(l.ts).toLocaleString()}</td>
                  <td className="px-4 py-3 font-semibold">{l.actor_name}</td>
                  <td className="px-4 py-3"><span className={`pill ${actionColor[l.action] || "bg-gray-100 text-gray-700"}`}>{l.action.replace(/_/g, " ")}</span></td>
                  <td className="px-4 py-3 text-xs text-[#86868B]">{l.target_type}{l.target_id ? `:${l.target_id.slice(0, 8)}` : ""}</td>
                  <td className="px-4 py-3 text-xs text-[#86868B] truncate max-w-xs">{Object.keys(l.details || {}).length > 0 ? JSON.stringify(l.details) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};



const RegionsTab = ({ regions, onChange, t }) => {
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const submit = async () => {
    if (!name) return;
    await api.post("/regions", { name }); toast.success("Added"); setShow(false); setName(""); onChange();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this region? Districts under it will be detached.")) return;
    await api.delete(`/regions/${id}`); toast.success("Deleted"); onChange();
  };
  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <h2 className="heading-font font-bold text-xl flex items-center gap-2"><Globe className="w-5 h-5 text-[#0055FF]" /> Regions</h2>
        <button onClick={() => setShow(true)} data-testid="add-region-btn" className="bg-[#0055FF] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Region
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-5">
        {regions.map(r => (
          <div key={r.id} className="flex items-center gap-3 p-4 bg-[#F5F5F7] rounded-2xl" data-testid={`region-${r.id}`}>
            <Globe className="w-5 h-5 text-[#0055FF]" />
            <div className="flex-1 font-bold heading-font">{r.name}</div>
            <button onClick={() => remove(r.id)} className="text-red-500 p-2 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {regions.length === 0 && <div className="col-span-full text-center text-[#86868B] py-8">No regions yet</div>}
      </div>
      {show && (
        <Modal onClose={() => setShow(false)} title="Add Region">
          <div className="space-y-3">
            <Input label="Region Name" value={name} onChange={setName} testid="region-name" />
            <button onClick={submit} data-testid="submit-region" className="w-full bg-[#0055FF] text-white rounded-xl py-3 font-bold">{t("save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

const StockTab = ({ products, stockLogs, lowStock, onChange, t }) => {
  const [adjusting, setAdjusting] = useState(null); // product
  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState(null); // product to view history for

  const submitAdjust = async () => {
    if (!adjusting || !delta) return;
    try {
      await api.post("/admin/stock/adjust", { product_id: adjusting.id, delta: Number(delta), reason: reason || "Manual adjustment" });
      toast.success("Stock adjusted");
      setAdjusting(null); setDelta(0); setReason("");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const productHistory = history ? stockLogs.filter(l => l.product_id === history.id) : [];

  return (
    <div className="space-y-5">
      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="low-stock-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-amber-900">{lowStock.length} product{lowStock.length > 1 ? "s" : ""} running low</div>
            <div className="text-xs text-amber-800 mt-0.5">{lowStock.slice(0, 5).map(p => `${p.name} (${p.stock_qty})`).join(" • ")}{lowStock.length > 5 ? "…" : ""}</div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h2 className="heading-font font-bold text-xl flex items-center gap-2"><Boxes className="w-5 h-5 text-[#0055FF]" /> Stock</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60">
              <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">SKU</th>
                <th className="px-4 py-3">Stock</th>
                <th className="px-4 py-3">Threshold</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.map(p => {
                const stock = p.stock_qty || 0;
                const thresh = p.low_stock_threshold || 20;
                const isLow = stock <= thresh;
                return (
                  <tr key={p.id} data-testid={`stock-row-${p.sku}`}>
                    <td className="px-4 py-3 font-semibold heading-font">{p.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{p.sku}</td>
                    <td className="px-4 py-3 font-bold text-lg">{stock} <span className="text-xs font-normal text-[#86868B]">{p.unit}</span></td>
                    <td className="px-4 py-3 text-[#86868B]">{thresh}</td>
                    <td className="px-4 py-3">
                      {isLow
                        ? <span className="pill bg-rose-50 text-rose-700">⚠ Low</span>
                        : <span className="pill bg-green-50 text-green-700">OK</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setAdjusting(p)} data-testid={`adjust-${p.sku}`} className="text-[#0055FF] hover:bg-blue-50 px-2 py-1 rounded-lg text-xs font-bold mr-1">+/− Adjust</button>
                      <button onClick={() => setHistory(p)} data-testid={`history-${p.sku}`} className="text-[#86868B] hover:bg-gray-50 px-2 py-1 rounded-lg text-xs font-bold">History</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {adjusting && (
        <Modal onClose={() => { setAdjusting(null); setDelta(0); setReason(""); }} title={`Adjust: ${adjusting.name}`}>
          <div className="space-y-3">
            <div className="text-sm text-[#86868B]">Current: <strong className="text-[#1D1D1F]">{adjusting.stock_qty || 0} {adjusting.unit}</strong></div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setDelta(d => Math.abs(Number(d) || 0))}
                className={`p-3 rounded-xl border-2 font-bold flex items-center justify-center gap-1 ${Number(delta) >= 0 ? "border-[#34C759] bg-green-50 text-[#34C759]" : "border-gray-200"}`}
                data-testid="stock-add-mode">
                <ArrowUp className="w-4 h-4" /> Add Stock
              </button>
              <button
                onClick={() => setDelta(d => -Math.abs(Number(d) || 0))}
                className={`p-3 rounded-xl border-2 font-bold flex items-center justify-center gap-1 ${Number(delta) < 0 ? "border-[#FF3B30] bg-rose-50 text-[#FF3B30]" : "border-gray-200"}`}
                data-testid="stock-reduce-mode">
                <ArrowDown className="w-4 h-4" /> Reduce
              </button>
            </div>
            <Input label="Quantity (+/-)" value={delta} onChange={v => setDelta(v)} type="number" testid="stock-delta" />
            <Input label="Reason" value={reason} onChange={setReason} testid="stock-reason" />
            <div className="text-sm text-[#86868B]">After: <strong className="text-[#1D1D1F]">{(adjusting.stock_qty || 0) + Number(delta || 0)} {adjusting.unit}</strong></div>
            <button onClick={submitAdjust} data-testid="submit-stock-adjust" className="w-full bg-[#0055FF] text-white rounded-xl py-3 font-bold">Confirm Adjustment</button>
          </div>
        </Modal>
      )}

      {history && (
        <Modal onClose={() => setHistory(null)} title={`History: ${history.name}`}>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {productHistory.length === 0 ? <div className="text-center text-[#86868B] py-6 text-sm">No history</div> :
              productHistory.map(l => (
                <div key={l.id} className="p-3 bg-[#F5F5F7] rounded-xl text-sm">
                  <div className="flex items-center justify-between">
                    <span className={`font-bold heading-font ${l.delta > 0 ? "text-[#34C759]" : "text-[#FF3B30]"}`}>
                      {l.delta > 0 ? "+" : ""}{l.delta}
                    </span>
                    <span className="text-xs text-[#86868B]">{new Date(l.ts).toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-[#1D1D1F] mt-0.5">{l.reason}</div>
                  <div className="text-xs text-[#86868B] mt-0.5">{l.before} → {l.after} • by {l.by_user_name}</div>
                </div>
              ))
            }
          </div>
        </Modal>
      )}
    </div>
  );
};

const PopupsTab = ({ popups, districts, onChange, t }) => {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", media_path: "", media_type: "image",
    enabled: true, start_date: "", end_date: "",
    audience: "all", district_ids: [],
  });
  const fileRef = React.useRef();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const isVideo = f.type.startsWith("video") || /\.(mp4|webm|mov)$/i.test(f.name);
      setForm(prev => ({ ...prev, media_path: r.data.path, media_type: isVideo ? "video" : "image" }));
      toast.success("Media uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const submit = async () => {
    if (!form.media_path) return toast.error("Upload an image or video first");
    if (form.audience === "district" && form.district_ids.length === 0)
      return toast.error("Select at least one district");
    try {
      const payload = { ...form };
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      await api.post("/admin/popups", payload);
      toast.success("Popup created");
      setShow(false);
      setForm({ title: "", media_path: "", media_type: "image", enabled: true,
                start_date: "", end_date: "", audience: "all", district_ids: [] });
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const toggle = async (p) => {
    await api.put(`/admin/popups/${p.id}`, { enabled: !p.enabled });
    toast.success(p.enabled ? "Disabled" : "Enabled");
    onChange();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this popup?")) return;
    await api.delete(`/admin/popups/${id}`);
    toast.success("Deleted");
    onChange();
  };

  const dmap = Object.fromEntries(districts.map(d => [d.id, d.name]));

  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="heading-font font-bold text-xl flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#0055FF]" /> Popups / {t("offer")}</h2>
          <p className="text-xs text-[#86868B] mt-0.5">Shown once per day to retailers / wholesalers. No discount logic — informational only.</p>
        </div>
        <button onClick={() => setShow(true)} data-testid="add-popup-btn" className="bg-[#0055FF] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Popup
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
        {popups.length === 0 && <div className="col-span-full text-center text-[#86868B] py-8 text-sm">No popups yet</div>}
        {popups.map(p => (
          <div key={p.id} className="bg-[#F5F5F7] rounded-2xl overflow-hidden border border-gray-100" data-testid={`popup-card-${p.id}`}>
            <div className="aspect-video bg-black grid place-items-center">
              {p.media_type === "video" ? (
                <Video className="w-10 h-10 text-white/60" />
              ) : (
                <img src={fileUrl(p.media_path)} alt={p.title || "popup"} className="w-full h-full object-cover" />
              )}
            </div>
            <div className="p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold heading-font truncate">{p.title || "(untitled)"}</div>
                <span className={`pill ${p.enabled ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {p.enabled ? "ON" : "OFF"}
                </span>
              </div>
              <div className="text-xs text-[#86868B]">
                {p.audience === "all" ? "All outlets" : `${p.district_ids.length} district(s)`}
                {p.start_date || p.end_date ? ` • ${p.start_date || "any"} → ${p.end_date || "any"}` : ""}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => toggle(p)} data-testid={`toggle-popup-${p.id}`} className="flex-1 bg-white hover:bg-gray-100 rounded-lg py-1.5 text-xs font-bold flex items-center justify-center gap-1">
                  <Power className="w-3 h-3" /> {p.enabled ? "Disable" : "Enable"}
                </button>
                <button onClick={() => remove(p.id)} data-testid={`delete-popup-${p.id}`} className="bg-rose-50 hover:bg-rose-100 text-[#FF3B30] rounded-lg py-1.5 px-2.5 text-xs font-bold">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {show && (
        <Modal onClose={() => setShow(false)} title="Add Offer Popup">
          <div className="space-y-3">
            <Input label="Title (optional)" value={form.title} onChange={v => setForm({...form, title: v})} testid="popup-title" />
            <div>
              <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">Media (image/video, max 32MB)</label>
              <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={upload} data-testid="popup-media-input" />
              <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="popup-upload-btn"
                className="w-full mt-1 bg-[#F5F5F7] hover:bg-blue-50 border-2 border-dashed border-gray-300 rounded-xl py-4 font-bold flex items-center justify-center gap-2 text-[#0055FF]">
                {form.media_path ? (
                  <><Check className="w-4 h-4" /> Uploaded ({form.media_type})</>
                ) : (busy ? "Uploading…" : <><Upload className="w-4 h-4" /> Choose file</>)}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">Start date</label>
                <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} data-testid="popup-start" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 mt-1" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">End date</label>
                <input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} data-testid="popup-end" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-3 mt-1" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#86868B] font-bold">Audience</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button onClick={() => setForm({...form, audience: "all", district_ids: []})}
                  data-testid="audience-all"
                  className={`p-3 rounded-xl border-2 font-bold text-sm ${form.audience === "all" ? "border-[#0055FF] bg-blue-50 text-[#0055FF]" : "border-gray-200"}`}>
                  All Outlets
                </button>
                <button onClick={() => setForm({...form, audience: "district"})}
                  data-testid="audience-district"
                  className={`p-3 rounded-xl border-2 font-bold text-sm ${form.audience === "district" ? "border-[#0055FF] bg-blue-50 text-[#0055FF]" : "border-gray-200"}`}>
                  By District
                </button>
              </div>
            </div>
            {form.audience === "district" && (
              <div className="bg-[#F5F5F7] rounded-xl p-3 max-h-40 overflow-y-auto">
                {districts.map(d => (
                  <label key={d.id} className="flex items-center gap-2 py-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.district_ids.includes(d.id)}
                      onChange={e => {
                        const ids = e.target.checked
                          ? [...form.district_ids, d.id]
                          : form.district_ids.filter(x => x !== d.id);
                        setForm({...form, district_ids: ids});
                      }}
                      data-testid={`district-check-${d.id}`}
                    />
                    <span className="text-sm font-semibold">{d.name}</span>
                  </label>
                ))}
              </div>
            )}
            <button onClick={submit} disabled={busy} data-testid="submit-popup" className="w-full bg-[#0055FF] disabled:opacity-50 text-white rounded-xl py-3 font-bold">{t("save")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
};



const ShopsTab = ({ shops, districts, onChange, t }) => {
  const { lang } = useLang();
  const [filter, setFilter] = useState("all"); // all | retailer | wholesaler | pending
  const [search, setSearch] = useState("");
  // Tier 2.1 (Feb 2026): Tamil-aware district label.
  const dmap = Object.fromEntries((districts || []).map(d => [d.id, (lang === "ta" && d.name_ta) ? d.name_ta : d.name]));
  const filtered = shops.filter(s => {
    if (filter === "retailer" && s.shop_type !== "retailer") return false;
    if (filter === "wholesaler" && s.shop_type !== "wholesaler") return false;
    if (filter === "pending" && s.approval_status !== "salesman_approved") return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(s.name || "").toLowerCase().includes(q) &&
          !((s.name_ta || "").toLowerCase().includes(q)) &&
          !(s.phone || "").includes(q)) return false;
    }
    return true;
  });
  const setType = async (id, shop_type) => {
    try {
      await api.put(`/shops/${id}/type`, { shop_type });
      toast.success(t("admin_approved"));
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const statusPill = (s) => {
    const status = s.approval_status || "admin_approved";
    const label = t(status) || status;
    const color = status === "salesman_approved" ? "bg-orange-50 text-orange-700"
      : status === "manager_approved" ? "bg-blue-50 text-blue-700"
      : "bg-green-50 text-green-700";
    return <span className={`pill ${color}`} data-testid={`shop-status-${s.id}`}>{label}</span>;
  };
  return (
    <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex flex-wrap items-center gap-3">
        <h2 className="heading-font font-bold text-xl flex items-center gap-2 mr-auto"><Store className="w-5 h-5 text-[#0055FF]" /> Shops ({filtered.length})</h2>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t("search")}
          data-testid="shops-search"
          className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm w-48"
        />
        <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="shops-filter" className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm">
          <option value="all">All</option>
          <option value="retailer">{t("retailer")}</option>
          <option value="wholesaler">{t("wholesaler")}</option>
          <option value="pending">{t("pending_approval")}</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50/60">
            <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
              <th className="px-4 py-3">{t("shop_name")}</th>
              <th className="px-4 py-3">{t("phone")}</th>
              <th className="px-4 py-3">{t("district")}</th>
              <th className="px-4 py-3">{t("shop_type")}</th>
              <th className="px-4 py-3">{t("approval_status")}</th>
              <th className="px-4 py-3">{t("approved_by")}</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(s => (
              <tr key={s.id} className="hover:bg-gray-50/40" data-testid={`admin-shop-${s.id}`}>
                <td className="px-4 py-3 font-semibold heading-font">
                  {(lang === "ta" && s.name_ta) ? s.name_ta : s.name}
                  {!s.name_ta && <span className="ml-2 text-[10px] text-amber-700 font-bold" title="Tamil name missing">⚠ TA</span>}
                </td>
                <td className="px-4 py-3">{s.phone || "—"}</td>
                <td className="px-4 py-3">{dmap[s.district_id] || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`pill ${s.shop_type === "wholesaler" ? "bg-purple-50 text-purple-700" : "bg-gray-100 text-gray-700"}`}>
                    {s.shop_type === "wholesaler" ? t("wholesaler") : t("retailer")}
                  </span>
                </td>
                <td className="px-4 py-3">{statusPill(s)}</td>
                <td className="px-4 py-3 text-xs text-[#86868B]">
                  {s.approved_by_name || s.approved_by_role || "—"}
                  {s.approved_at && <div>{new Date(s.approved_at).toLocaleDateString()}</div>}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1.5">
                    <button
                      onClick={() => setType(s.id, "retailer")}
                      data-testid={`set-retailer-${s.id}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${s.shop_type === "retailer" ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white border-gray-300 hover:border-[#0055FF]"}`}
                    >
                      {t("retailer")}
                    </button>
                    <button
                      onClick={() => setType(s.id, "wholesaler")}
                      data-testid={`set-wholesaler-${s.id}`}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${s.shop_type === "wholesaler" ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white border-gray-300 hover:border-[#0055FF]"}`}
                    >
                      {t("wholesaler")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-[#86868B]">No shops</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DangerZoneTab = ({ t, districts, onChange }) => {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(false);
  const [unlockText, setUnlockText] = useState("");
  const [trash, setTrash] = useState({ orders: [], users: [], shops: [], products: [] });

  // Bulk delete modal
  const [showBulk, setShowBulk] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkFilter, setBulkFilter] = useState({ status: "", district_id: "", start_date: "", end_date: "", reason: "" });

  // Wipe demo modal
  const [showWipe, setShowWipe] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");

  // Factory reset modal (2-step)
  const [showReset, setShowReset] = useState(false);
  const [resetStep, setResetStep] = useState(1);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetPhone, setResetPhone] = useState("");

  const loadTrash = async () => {
    try {
      const r = await api.get("/admin/trash");
      setTrash(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { if (unlocked) loadTrash(); }, [unlocked]);

  const downloadBackup = async (format) => {
    try {
      const r = await api.get(`/admin/danger/backup?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const ext = format === "json" ? "json" : "zip";
      a.download = `gtroyal-backup-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const restoreOrder = async (id) => {
    await api.post(`/admin/orders/${id}/restore`); toast.success(t("restore")); loadTrash();
  };
  const restoreShop = async (id) => {
    await api.post(`/admin/shops/${id}/restore`); toast.success(t("restore")); loadTrash();
  };
  const restoreUser = async (id) => {
    await api.post(`/users/${id}/restore`); toast.success(t("restore")); loadTrash(); onChange();
  };

  const submitBulk = async () => {
    if (bulkConfirm !== "DELETE") return toast.error('Type "DELETE" exactly');
    const payload = { confirmation: bulkConfirm };
    Object.entries(bulkFilter).forEach(([k, v]) => { if (v) payload[k] = v; });
    if (!payload.status && !payload.district_id && !payload.start_date && !payload.end_date) {
      return toast.error("Set at least one filter");
    }
    try {
      const r = await api.post("/admin/danger/bulk-delete-orders", payload);
      toast.success(`Deleted ${r.data.deleted} orders`);
      setShowBulk(false); setBulkConfirm(""); setBulkFilter({ status: "", district_id: "", start_date: "", end_date: "", reason: "" });
      loadTrash(); onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const submitWipe = async () => {
    if (wipeConfirm !== "DELETE") return toast.error('Type "DELETE" exactly');
    try {
      const r = await api.post("/admin/danger/wipe-demo-data", { confirmation: wipeConfirm });
      toast.success(`Wiped: ${JSON.stringify(r.data.deleted)}`);
      setShowWipe(false); setWipeConfirm("");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const submitReset = async () => {
    if (resetConfirm !== "ERASE ALL DATA") return toast.error('Type "ERASE ALL DATA" exactly');
    if (resetPhone !== user.phone) return toast.error("Phone mismatch");
    if (!window.confirm(`FINAL WARNING: This will wipe ALL business data and cannot be undone. Continue?`)) return;
    try {
      // Step 0: Auto-backup
      await downloadBackup("both");
      const r = await api.post("/admin/danger/factory-reset", { confirmation: resetConfirm, super_admin_phone: resetPhone });
      toast.success(`Reset complete. Wiped: ${JSON.stringify(r.data.wiped)}`);
      setShowReset(false); setResetStep(1); setResetConfirm(""); setResetPhone("");
      onChange();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  if (!unlocked) {
    return (
      <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
        <div className="p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 grid place-items-center">
            <AlertOctagon className="w-9 h-9 text-red-600" />
          </div>
          <h2 className="heading-font font-bold text-2xl text-red-700 mb-2">{t("danger_zone")}</h2>
          <p className="text-[#86868B] mb-6 max-w-md mx-auto">{t("danger_zone_warning")}</p>
          <input
            value={unlockText}
            onChange={e => setUnlockText(e.target.value)}
            placeholder='Type "I UNDERSTAND" to unlock'
            data-testid="dz-unlock-input"
            className="border-2 border-red-200 rounded-xl px-4 py-3 text-center font-bold w-full max-w-sm focus:ring-2 focus:ring-red-300 outline-none"
          />
          <button
            disabled={unlockText !== "I UNDERSTAND"}
            onClick={() => setUnlocked(true)}
            data-testid="dz-unlock-btn"
            className="mt-3 block mx-auto bg-red-600 hover:bg-red-700 disabled:opacity-30 text-white px-6 py-3 rounded-xl font-bold"
          >
            Unlock {t("danger_zone")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="danger-zone">
      {/* Backup */}
      <div className="bg-white rounded-2xl soft-shadow p-6" data-testid="dz-backup-card">
        <h3 className="heading-font font-bold text-lg mb-2 flex items-center gap-2"><Database className="w-5 h-5 text-[#0055FF]" /> {t("backup_download")}</h3>
        <p className="text-sm text-[#86868B] mb-4">Export a full snapshot of all data before risky operations.</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="dz-backup-json" onClick={() => downloadBackup("json")} className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> {t("backup_format_json")}
          </button>
          <button data-testid="dz-backup-csv" onClick={() => downloadBackup("csv")} className="bg-white border border-[#0055FF] text-[#0055FF] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> {t("backup_format_csv")}
          </button>
          <button data-testid="dz-backup-both" onClick={() => downloadBackup("both")} className="bg-white border border-[#0055FF] text-[#0055FF] rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> {t("backup_format_both")}
          </button>
        </div>
      </div>

      {/* Trash */}
      <div className="bg-white rounded-2xl soft-shadow p-6" data-testid="dz-trash-card">
        <h3 className="heading-font font-bold text-lg mb-2 flex items-center gap-2"><Trash2 className="w-5 h-5 text-orange-600" /> {t("trash")} ({(trash.orders?.length || 0) + (trash.users?.length || 0) + (trash.shops?.length || 0)})</h3>
        <div className="grid md:grid-cols-3 gap-4 mt-3">
          <div>
            <div className="text-xs text-[#86868B] uppercase tracking-wider mb-1">Orders ({trash.orders?.length || 0})</div>
            <div className="space-y-1 max-h-48 overflow-auto">
              {(trash.orders || []).slice(0, 30).map(o => (
                <div key={o.id} className="text-xs flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                  <span className="truncate">{o.shop_name} • {o.id.slice(0, 6)}</span>
                  <button data-testid={`restore-order-${o.id}`} onClick={() => restoreOrder(o.id)} className="text-[#0055FF] font-bold"><RotateCcw className="w-3 h-3 inline" /> {t("restore")}</button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-[#86868B] uppercase tracking-wider mb-1">Users ({trash.users?.length || 0})</div>
            <div className="space-y-1 max-h-48 overflow-auto">
              {(trash.users || []).slice(0, 30).map(u => (
                <div key={u.id} className="text-xs flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                  <span className="truncate">{u.name} ({u.role})</span>
                  <button data-testid={`restore-trash-user-${u.phone}`} onClick={() => restoreUser(u.id)} className="text-[#0055FF] font-bold"><RotateCcw className="w-3 h-3 inline" /></button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs text-[#86868B] uppercase tracking-wider mb-1">Shops ({trash.shops?.length || 0})</div>
            <div className="space-y-1 max-h-48 overflow-auto">
              {(trash.shops || []).slice(0, 30).map(s => (
                <div key={s.id} className="text-xs flex items-center justify-between bg-gray-50 rounded px-2 py-1">
                  <span className="truncate">{s.name}</span>
                  <button data-testid={`restore-shop-${s.id}`} onClick={() => restoreShop(s.id)} className="text-[#0055FF] font-bold"><RotateCcw className="w-3 h-3 inline" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Delete Orders */}
      <div className="bg-white rounded-2xl soft-shadow p-6 border-l-4 border-orange-500">
        <h3 className="heading-font font-bold text-lg mb-2 flex items-center gap-2 text-orange-700"><Trash2 className="w-5 h-5" /> {t("bulk_delete_orders")}</h3>
        <p className="text-sm text-[#86868B] mb-4">Soft-delete orders matching filters. Recoverable from Trash above.</p>
        <button data-testid="dz-bulk-open" onClick={() => setShowBulk(true)} className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl px-4 py-2 font-bold text-sm">{t("bulk_delete_orders")}</button>
      </div>

      {/* Wipe Demo */}
      <div className="bg-white rounded-2xl soft-shadow p-6 border-l-4 border-orange-500">
        <h3 className="heading-font font-bold text-lg mb-2 flex items-center gap-2 text-orange-700"><Trash2 className="w-5 h-5" /> {t("wipe_demo_data")}</h3>
        <p className="text-sm text-[#86868B] mb-4">Hard-delete records prefixed with TEST_ or DEMO_. Irreversible.</p>
        <button data-testid="dz-wipe-open" onClick={() => setShowWipe(true)} className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl px-4 py-2 font-bold text-sm">{t("wipe_demo_data")}</button>
      </div>

      {/* Factory Reset */}
      <div className="bg-white rounded-2xl soft-shadow p-6 border-l-4 border-red-600">
        <h3 className="heading-font font-bold text-lg mb-2 flex items-center gap-2 text-red-700"><AlertOctagon className="w-5 h-5" /> {t("factory_reset")}</h3>
        <p className="text-sm text-[#86868B] mb-4">Hard-deletes ALL business data (orders, shops, products, users) and re-seeds the demo. Auto-downloads a backup first.</p>
        <button data-testid="dz-reset-open" onClick={() => { setShowReset(true); setResetStep(1); }} className="bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2 font-bold text-sm">{t("factory_reset")}</button>
      </div>

      {/* Modals */}
      {showBulk && (
        <Modal onClose={() => setShowBulk(false)} title={t("bulk_delete_orders")}>
          <div className="space-y-3">
            <select value={bulkFilter.status} onChange={e => setBulkFilter({ ...bulkFilter, status: e.target.value })} data-testid="bulk-status" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="assigned">Assigned</option>
              <option value="delivered">Delivered</option>
            </select>
            <select value={bulkFilter.district_id} onChange={e => setBulkFilter({ ...bulkFilter, district_id: e.target.value })} data-testid="bulk-district" className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2">
              <option value="">All districts</option>
              {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <Input label="Start date" value={bulkFilter.start_date} onChange={v => setBulkFilter({ ...bulkFilter, start_date: v })} testid="bulk-start" />
            <Input label="End date" value={bulkFilter.end_date} onChange={v => setBulkFilter({ ...bulkFilter, end_date: v })} testid="bulk-end" />
            <Input label="Reason (optional)" value={bulkFilter.reason} onChange={v => setBulkFilter({ ...bulkFilter, reason: v })} testid="bulk-reason" />
            <Input label='Type "DELETE" to confirm' value={bulkConfirm} onChange={setBulkConfirm} testid="bulk-confirm" />
            <button onClick={submitBulk} data-testid="bulk-submit" disabled={bulkConfirm !== "DELETE"} className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-30 text-white rounded-xl py-3 font-bold">{t("yes_delete")}</button>
          </div>
        </Modal>
      )}

      {showWipe && (
        <Modal onClose={() => setShowWipe(false)} title={t("wipe_demo_data")}>
          <div className="space-y-3">
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-sm text-orange-800">
              Will hard-delete records whose name starts with TEST_ or DEMO_. This is IRREVERSIBLE.
            </div>
            <Input label='Type "DELETE" to confirm' value={wipeConfirm} onChange={setWipeConfirm} testid="wipe-confirm" />
            <button onClick={submitWipe} data-testid="wipe-submit" disabled={wipeConfirm !== "DELETE"} className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-30 text-white rounded-xl py-3 font-bold">{t("yes_delete")}</button>
          </div>
        </Modal>
      )}

      {showReset && (
        <Modal onClose={() => { setShowReset(false); setResetStep(1); }} title={t("factory_reset")}>
          {resetStep === 1 && (
            <div className="space-y-3">
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4 text-sm text-red-800">
                <strong>This wipes ALL business data:</strong> orders, shops, products, districts, regions, areas, territories, beats, stock logs, popups, notifications, and all non-admin users. The Super Admin account survives. A backup will auto-download first.
              </div>
              <button onClick={() => setResetStep(2)} data-testid="reset-step1-next" className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl py-3 font-bold">I understand — Continue</button>
            </div>
          )}
          {resetStep === 2 && (
            <div className="space-y-3">
              <Input label='Type "ERASE ALL DATA" to confirm' value={resetConfirm} onChange={setResetConfirm} testid="reset-confirm" />
              <Input label={`${t("confirm_phone")} (${user.phone})`} value={resetPhone} onChange={setResetPhone} testid="reset-phone" />
              <button onClick={submitReset} data-testid="reset-submit" disabled={resetConfirm !== "ERASE ALL DATA" || resetPhone !== user.phone} className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-30 text-white rounded-xl py-3 font-bold">{t("yes_delete")} — {t("factory_reset")}</button>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};


// Phase 12B: Stock by District Depot dashboard
const DepotStockTab = ({ t }) => {
  const [summary, setSummary] = useState([]);
  const [depots, setDepots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pickedDepot, setPickedDepot] = useState(null);
  const [pickedCategory, setPickedCategory] = useState(null);
  const [stock, setStock] = useState({ items: [] });
  const [showReceive, setShowReceive] = useState(false);
  const [receiveForm, setReceiveForm] = useState({ product_id: "", quantity: "", note: "" });
  const [adjusting, setAdjusting] = useState(null); // product
  const [adjustQty, setAdjustQty] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [history, setHistory] = useState(null); // {product_id, name}
  const [historyRows, setHistoryRows] = useState([]);

  const loadAll = async () => {
    const [s, d, c] = await Promise.all([
      api.get("/depot-stock-summary"),
      api.get("/depots"),
      api.get("/categories"),
    ]);
    setSummary(s.data); setDepots(d.data); setCategories(c.data);
  };
  useEffect(() => { loadAll(); }, []);

  const loadDepotStock = async (depotId) => {
    const r = await api.get(`/depot-stock/${depotId}`);
    setStock(r.data);
  };

  const pickDepot = async (d) => {
    setPickedDepot(d); setPickedCategory(null);
    await loadDepotStock(d.id);
  };

  const submitReceive = async () => {
    if (!receiveForm.product_id || !receiveForm.quantity) return toast.error("Fill all fields");
    try {
      await api.post("/depot-stock/receive", {
        depot_id: pickedDepot.id,
        product_id: receiveForm.product_id,
        quantity: Number(receiveForm.quantity),
        note: receiveForm.note || null,
      });
      toast.success(t("receive_stock"));
      setShowReceive(false); setReceiveForm({ product_id: "", quantity: "", note: "" });
      loadAll(); loadDepotStock(pickedDepot.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const submitAdjust = async () => {
    try {
      await api.post("/depot-stock/adjust", {
        depot_id: pickedDepot.id,
        product_id: adjusting.product_id,
        new_qty: Number(adjustQty),
        reason: adjustReason || "Manual adjustment",
      });
      toast.success("Stock adjusted");
      setAdjusting(null); setAdjustQty(""); setAdjustReason("");
      loadDepotStock(pickedDepot.id); loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const openHistory = async (item) => {
    setHistory(item);
    const r = await api.get(`/depot-stock/history/${pickedDepot.id}/${item.product_id}`);
    setHistoryRows(r.data);
  };

  const cmap = Object.fromEntries(categories.map(c => [c.id, c]));

  // ====== Step 1: Depot Cards ======
  if (!pickedDepot) {
    return (
      <div className="space-y-4">
        <h2 className="heading-font font-bold text-2xl text-[#1D1D1F]" data-testid="depot-stock-heading">{t("depots")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {summary.map(d => (
            <button
              key={d.depot_id}
              onClick={() => pickDepot({ id: d.depot_id, name: d.name })}
              data-testid={`depot-card-${d.depot_id}`}
              className="bg-white rounded-2xl p-5 soft-shadow text-left hover:scale-[1.02] transition"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="heading-font font-bold text-lg text-[#1D1D1F]">{d.name}</h3>
                {d.low_count > 0 && (
                  <span className="pill bg-amber-50 text-amber-700 text-xs font-bold">⚠ {d.low_count} low</span>
                )}
              </div>
              <div className="space-y-1.5">
                {d.by_category.length === 0 && <div className="text-xs text-[#86868B]">No categorised stock</div>}
                {d.by_category.map(c => (
                  <div key={c.id} className="flex items-center justify-between text-sm">
                    <span className="text-[#86868B]">{c.name}</span>
                    <span className="font-bold text-[#1D1D1F] heading-font">{c.qty.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ====== Step 2: Categories grid ======
  if (!pickedCategory) {
    const visibleProducts = stock.items;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setPickedDepot(null)} data-testid="back-to-depots" className="text-[#0055FF] font-bold text-sm">← {t("depots")}</button>
          <h2 className="heading-font font-bold text-2xl text-[#1D1D1F]">{pickedDepot.name}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {categories.map(c => {
            const itemsInCat = visibleProducts.filter(p => p.category_id === c.id);
            const total = itemsInCat.reduce((s, p) => s + p.qty, 0);
            const low = itemsInCat.filter(p => p.low).length;
            return (
              <button
                key={c.id}
                onClick={() => setPickedCategory(c)}
                data-testid={`category-card-${c.id}`}
                className="bg-white rounded-2xl p-5 soft-shadow text-left hover:scale-[1.02] transition"
              >
                <div className="heading-font font-bold text-lg text-[#1D1D1F]">{c.name_en}</div>
                {c.name_ta && <div className="text-sm text-[#86868B]">{c.name_ta}</div>}
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-[#86868B]">{itemsInCat.length} products</span>
                  <span className="heading-font font-bold text-2xl">{total.toLocaleString()}</span>
                </div>
                {low > 0 && <div className="mt-2 text-xs font-bold text-amber-700">⚠ {low} low stock</div>}
              </button>
            );
          })}
        </div>
        <div className="pt-2">
          <button onClick={() => setShowReceive(true)} data-testid="open-receive-stock" className="bg-[#34C759] hover:bg-[#2EAF4E] text-white rounded-xl px-5 py-3 font-bold flex items-center gap-2">
            <Plus className="w-5 h-5" /> {t("receive_stock")}
          </button>
        </div>

        {showReceive && (
          <Modal onClose={() => setShowReceive(false)} title={t("receive_stock") + " — " + pickedDepot.name}>
            <div className="space-y-3">
              <select
                value={receiveForm.product_id}
                onChange={e => setReceiveForm({ ...receiveForm, product_id: e.target.value })}
                data-testid="receive-product"
                className="w-full bg-[#F5F5F7] rounded-xl px-3 py-2"
              >
                <option value="">{t("pick_product")}</option>
                {visibleProducts.map(p => <option key={p.product_id} value={p.product_id}>{p.name} (current: {p.qty})</option>)}
              </select>
              <Input label={t("quantity_received")} value={receiveForm.quantity} onChange={v => setReceiveForm({ ...receiveForm, quantity: v })} type="number" testid="receive-qty" />
              <Input label="Note (optional)" value={receiveForm.note} onChange={v => setReceiveForm({ ...receiveForm, note: v })} testid="receive-note" />
              <button onClick={submitReceive} data-testid="submit-receive" className="w-full bg-[#34C759] hover:bg-[#2EAF4E] text-white rounded-xl py-3 font-bold">{t("save_stock_entry")}</button>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  // ====== Step 3: Products inside category ======
  const productsInCategory = stock.items.filter(p => p.category_id === pickedCategory.id);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <button onClick={() => setPickedDepot(null)} className="text-[#0055FF] font-bold text-sm">← {t("depots")}</button>
        <span className="text-[#86868B]">/</span>
        <button onClick={() => setPickedCategory(null)} className="text-[#0055FF] font-bold text-sm">{pickedDepot.name}</button>
        <span className="text-[#86868B]">/</span>
        <h2 className="heading-font font-bold text-xl text-[#1D1D1F]">{pickedCategory.name_en}</h2>
      </div>
      <div className="space-y-2">
        {productsInCategory.map(p => (
          <div key={p.product_id} className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-4" data-testid={`stock-row-${p.product_id}`}>
            {p.image_url && <img src={imageSrc(p.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="font-bold heading-font truncate">{p.name}</div>
              <div className="text-xs text-[#86868B]">{p.sku} • {p.unit}</div>
              <div className="text-xs flex gap-3 mt-1">
                <span className="text-green-700">+{p.received_today} {t("receive_stock_today")}</span>
                <span className="text-orange-700">−{p.sold_today} {t("sold_today")}</span>
              </div>
            </div>
            <div className="text-right">
              <div className={`heading-font font-bold text-2xl ${p.qty <= 0 ? "text-red-600" : p.low ? "text-amber-700" : "text-[#1D1D1F]"}`}>
                {p.qty <= 0 ? <span className="text-base">{t("not_enough_stock")}</span> : p.qty.toLocaleString()}
              </div>
              <div className="flex gap-2 mt-1">
                <button onClick={() => { setAdjusting(p); setAdjustQty(String(p.qty)); }} data-testid={`adjust-${p.product_id}`} className="text-xs font-bold text-[#0055FF] hover:bg-blue-50 px-2 py-1 rounded">{t("edit")}</button>
                <button onClick={() => openHistory(p)} data-testid={`history-${p.product_id}`} className="text-xs font-bold text-[#86868B] hover:bg-gray-100 px-2 py-1 rounded">{t("stock_history")}</button>
              </div>
            </div>
          </div>
        ))}
        {productsInCategory.length === 0 && <div className="text-[#86868B] text-center py-8">No products</div>}
      </div>

      {adjusting && (
        <Modal onClose={() => setAdjusting(null)} title={t("override_low_stock") + " — " + adjusting.name}>
          <div className="space-y-3">
            <Input label="New Quantity" value={adjustQty} onChange={setAdjustQty} type="number" testid="adjust-new-qty" />
            <Input label="Reason" value={adjustReason} onChange={setAdjustReason} testid="adjust-reason" />
            <button onClick={submitAdjust} data-testid="submit-adjust" className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-xl py-3 font-bold">Save</button>
          </div>
        </Modal>
      )}

      {history && (
        <Modal onClose={() => setHistory(null)} title={t("stock_history") + " — " + history.name}>
          <div className="space-y-2 max-h-96 overflow-auto">
            {historyRows.length === 0 && <div className="text-[#86868B] text-center py-4">No history</div>}
            {historyRows.map(h => (
              <div key={h.id} className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-[#86868B]">{new Date(h.ts).toLocaleString()}</div>
                  <div className="truncate">{h.reason}</div>
                  <div className="text-xs text-[#86868B]">by {h.by_user_name}</div>
                </div>
                <div className={`font-bold heading-font text-lg ${h.delta > 0 ? "text-green-700" : "text-orange-700"}`}>
                  {h.delta > 0 ? "+" : ""}{h.delta}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};
